import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  Clock3,
  Edit3,
  IndianRupee,
  Layers3,
  Package,
  Plus,
  Power,
  RefreshCcw,
  Search,
  Tag,
  Trash2,
  X
} from 'lucide-react';
import EmptyState from '../../../shared/components/ui/EmptyState';
import Skeleton from '../../../shared/components/ui/Skeleton';
import {
  PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICE,
  PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICES,
  PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICES_BY_CATEGORY,
  PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICE_STATUS,
  PROFILE_STORES_APPOINTMENTS_CATEGORIES,
  PROFILE_STORES_APPOINTMENTS_CATEGORIES_SORT,
  PROFILE_STORES_APPOINTMENTS_CATEGORY
} from '../../../shared/constants/endpoints';
import AssignmentManagerModal from '../../employees/components/AssignmentManagerModal';
import { useEmployees, useRemoveAssignment, useServiceAssignments } from '../../employees/queries';
import { env } from '../../../config/env';
import './StoreServicesCatalog.css';

const CatalogLoadingSkeleton = () => (
  <section className="services-catalog" style={{ display: 'grid', gap: 20, minHeight: '100%', background: '#000000' }} aria-label="Loading services catalog">
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
      {Array.from({ length: 3 }).map((_, index) => (
        <article
          key={`catalog-stat-skeleton-${index}`}
          style={{
            border: '1px solid rgba(148, 163, 184, 0.18)',
            borderRadius: 18,
            padding: 16,
            background: 'rgba(7, 7, 7, 0.98)'
          }}
        >
          <Skeleton height={12} width="45%" />
          <Skeleton height={28} width="35%" />
          <Skeleton height={12} width="70%" />
        </article>
      ))}
    </div>

    <article
      style={{
        border: '1px solid rgba(148, 163, 184, 0.18)',
        borderRadius: 18,
        padding: 16,
        background: 'rgba(7, 7, 7, 0.98)'
      }}
    >
      <Skeleton height={18} width={220} />
      <div style={{ display: 'grid', gap: 12, marginTop: 18 }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={`catalog-row-skeleton-${index}`} height={56} width="100%" />
        ))}
      </div>
    </article>
  </section>
);

const pageShellStyle = {
  display: 'grid',
  gap: 16,
  minHeight: '100%',
  background: '#000000'
};

const EMPTY_CATEGORY_DRAFT = {
  name: '',
  description: '',
  isVisible: true
};

const EMPTY_SERVICE_DRAFT = {
  categoryUuid: '',
  serviceName: '',
  serviceDescription: '',
  appointmentDurationMins: 30,
  bookingMode: 'APPOINTMENT',
  priceDisplayType: 'EXACT',
  basePrice: '0',
  maxBookingsPerSlot: 1,
  selfBookable: true,
  requiresConsultation: false,
  isAddon: false,
  addonRequiresServiceUuid: '',
  assignableRoleIdsText: ''
};

const inputStyle = {
  width: '100%',
  borderRadius: 12,
  border: '1px solid rgba(148, 163, 184, 0.2)',
  background: 'rgba(10, 10, 10, 0.98)',
  color: 'inherit',
  padding: '11px 13px',
  outline: 'none'
};

const textAreaStyle = {
  resize: 'vertical',
  minHeight: 96,
  fontFamily: 'inherit'
};

const buttonStyle = (tone = 'default') => {
  const tones = {
    default: {
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background: 'rgba(15, 23, 42, 0.56)',
      color: 'inherit'
    },
    primary: {
      border: '1px solid rgba(243, 119, 77, 0.24)',
      background: 'rgba(243, 119, 77, 0.14)',
      color: '#F3774D'
    },
    danger: {
      border: '1px solid rgba(248, 113, 113, 0.24)',
      background: 'rgba(127, 29, 29, 0.22)',
      color: '#fca5a5'
    }
  };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    padding: '10px 14px',
    cursor: 'pointer',
    fontFamily: 'Sora, system-ui, sans-serif',
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.2,
    ...tones[tone]
  };
};

const isActiveCatalogService = (service) =>
  String(service?.catalogStatus || '').toUpperCase() === 'ACTIVE';

/**
 * Does a service row survive the catalog's status filter?
 *
 * Catalog filter semantics (S1, June 2026):
 *  - ALL     → every row, regardless of status.
 *  - ACTIVE  → every row in active categories (the catalog-level filter still
 *              hides the row's parent if the *category* is INACTIVE); a row's
 *              own catalogStatus is irrelevant here so deactivated services
 *              stay visible with an INACTIVE chip rather than disappearing.
 *  - PAUSED  → only rows whose own catalogStatus is INACTIVE (inspection /
 *              cleanup view).
 *
 * Exported so the predicate is unit-testable without rendering the full page.
 */
export const matchesServiceStatusFilter = (catalogStatus, statusFilter) => {
  const filter = String(statusFilter || 'ALL').toUpperCase();
  if (filter === 'PAUSED') {
    return String(catalogStatus || '').toUpperCase() === 'INACTIVE';
  }
  return true;
};

export const isTruthyCatalogFlag = (value, fallback = false) => {
  if (value == null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === '') return fallback;
  if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
  return fallback;
};

export const isCustomerSelfBookableService = (service) =>
  isTruthyCatalogFlag(service?.selfBookable, true) && !isTruthyCatalogFlag(service?.requiresConsultation, false);

const formatBookingMode = (value) => {
  const normalized = String(value || '').toUpperCase();
  if (normalized === 'QUEUE') return 'Queue';
  if (normalized === 'BOTH') return 'Appointment + Queue';
  return 'Appointment';
};

const formatServicePriceLabel = (service) => {
  const normalized = String(service?.priceDisplayType || '').toUpperCase();
  if (normalized === 'ON_CONSULTATION') {
    return 'On consultation';
  }
  if (normalized === 'FREE') {
    return 'Free';
  }
  const amount = service?.basePrice ?? 0;
  if (normalized === 'STARTING_FROM') {
    return `From ${amount}/-`;
  }
  return `${amount}/-`;
};

const formatBookableByLabel = (service) => {
  if (Array.isArray(service?.assignableRoleIds) && service.assignableRoleIds.length) {
    return service.assignableRoleIds.length === 1 ? 'Selected role' : 'Selected roles';
  }
  return 'Any team member';
};

const resolveErrorMessage = (error, fallbackMessage) => {
  const payload = error?.data;
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload?.error === 'string' && payload.error.trim()) {
    return payload.error;
  }
  if (payload && typeof payload === 'object' && payload.errors && typeof payload.errors === 'object') {
    const firstFieldErrors = Object.values(payload.errors).find((value) => Array.isArray(value) && value.length);
    if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
      return String(firstFieldErrors[0]);
    }
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return fallbackMessage;
};

const normalizeDuration = (value) => {
  const next = Number.parseInt(String(value || '').trim(), 10);
  if (!Number.isFinite(next)) {
    return 30;
  }
  return Math.min(480, Math.max(5, next));
};

const normalizePositiveCount = (value, fallback = 1) => {
  const next = Number.parseInt(String(value || '').trim(), 10);
  if (!Number.isFinite(next)) {
    return fallback;
  }
  return Math.min(50, Math.max(1, next));
};

const normalizeBasePrice = (value, priceDisplayType) => {
  const normalizedType = String(priceDisplayType || 'EXACT').toUpperCase();
  if (normalizedType === 'FREE' || normalizedType === 'ON_CONSULTATION') {
    return 0;
  }
  const next = Number.parseFloat(String(value || '').trim());
  if (!Number.isFinite(next) || next < 0) {
    return 0;
  }
  return Number(next.toFixed(2));
};

const moveIndex = (items, fromIndex, toIndex) => {
  if (!Array.isArray(items) || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const ServiceAssignmentChips = ({
  serviceUuid,
  employeeNameById,
  onToast,
  onRequestRemove,
}) => {
  const assignmentsQuery = useServiceAssignments(serviceUuid);
  const removeMutation = useRemoveAssignment();

  const rows = Array.isArray(assignmentsQuery.data) ? assignmentsQuery.data : [];

  if (assignmentsQuery.isLoading || assignmentsQuery.isError || rows.length === 0) {
    return null;
  }

  const handleRemove = (row) => {
    const employeeLabel = employeeNameById[row.employeeUuid] || row.employeeUuid;
    onRequestRemove({
      title: 'Remove assigned employee?',
      description: `Remove ${employeeLabel} from this service? They will no longer appear as assigned here.`,
      confirmLabel: 'Remove employee',
      tone: 'danger',
      onConfirm: async () => {
        await removeMutation.mutateAsync({
          employeeUuid: row.employeeUuid,
          serviceUuid: row.serviceUuid,
        });
        onToast?.({ message: 'Assigned employee removed.', variant: 'success' });
      },
    });
  };

  return (
    <div className="catalog-assignment-block">
      <div className="catalog-assignment-block__title">Assigned employees</div>
      <div className="catalog-assignment-block__chips">
        {rows.map((row) => {
          const employeeLabel = employeeNameById[row.employeeUuid] || row.employeeUuid;
          return (
            <span key={row.assignmentUuid} className="catalog-assignment-chip">
              <span>{employeeLabel}</span>
              <button
                type="button"
                className="catalog-assignment-chip__remove"
                onClick={() => handleRemove(row)}
                disabled={removeMutation.isPending}
                aria-label={`Remove ${employeeLabel}`}
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
};

const buildServicePayloadFromRecord = (service, categoryUuid, displayOrder) => {
  const normalizedPriceDisplayType = String(service?.priceDisplayType || 'EXACT').toUpperCase();
  const requiresConsultation = isTruthyCatalogFlag(service?.requiresConsultation, false);
  const isAddon = isTruthyCatalogFlag(service?.isAddon, false);
  return {
    categoryUuid,
    serviceName: service?.serviceName || '',
    serviceDescription: service?.serviceDescription || null,
    imageUrl: service?.imageUrl || null,
    appointmentDurationMins: normalizeDuration(service?.appointmentDurationMins),
    bookingMode: service?.bookingMode || 'APPOINTMENT',
    priceDisplayType: normalizedPriceDisplayType,
    basePrice: normalizeBasePrice(service?.basePrice, normalizedPriceDisplayType),
    maxBookingsPerSlot: normalizePositiveCount(service?.maxBookingsPerSlot, 1),
    selfBookable: requiresConsultation ? false : isTruthyCatalogFlag(service?.selfBookable, true),
    requiresConsultation,
    isAddon,
    addonRequiresServiceUuid: isAddon ? (service?.addonRequiresServiceUuid || null) : null,
    assignableRoleIds: Array.isArray(service?.assignableRoleIds) ? service.assignableRoleIds : [],
    displayOrder
  };
};

const buildServiceDraftFromService = (service) => ({
  categoryUuid: service?.categoryUuid || '',
  serviceName: service?.serviceName || '',
  serviceDescription: service?.serviceDescription || '',
  appointmentDurationMins: Number(service?.appointmentDurationMins) || 30,
  bookingMode: String(service?.bookingMode || 'APPOINTMENT').toUpperCase(),
  priceDisplayType: String(service?.priceDisplayType || 'EXACT').toUpperCase(),
  basePrice: service?.basePrice == null ? '0' : String(service.basePrice),
  maxBookingsPerSlot: Number(service?.maxBookingsPerSlot) || 1,
  selfBookable: isTruthyCatalogFlag(service?.selfBookable, true),
  requiresConsultation: isTruthyCatalogFlag(service?.requiresConsultation, false),
  isAddon: isTruthyCatalogFlag(service?.isAddon, false),
  addonRequiresServiceUuid: service?.addonRequiresServiceUuid || '',
  assignableRoleIdsText: Array.isArray(service?.assignableRoleIds) ? service.assignableRoleIds.join(', ') : ''
});

const StoreServicesCatalog = ({
  request,
  storeToken,
  onToast,
  staffingEnabled = env.servicesEmployeesRolesEnabled,
  title = 'Services'
}) => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [categoryDraft, setCategoryDraft] = useState(EMPTY_CATEGORY_DRAFT);
  const [editingCategoryUuid, setEditingCategoryUuid] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [serviceDraft, setServiceDraft] = useState(EMPTY_SERVICE_DRAFT);
  const [editingServiceUuid, setEditingServiceUuid] = useState('');
  const [isSavingService, setIsSavingService] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [assignmentsForService, setAssignmentsForService] = useState(null);
  const [sequenceDraft, setSequenceDraft] = useState([]);
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [activeActionKey, setActiveActionKey] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const canManage = typeof request === 'function' && Boolean(storeToken);
  const staffingEmployeesQuery = useEmployees(staffingEnabled, {});

  const resetCategoryDraft = useCallback(() => {
    setCategoryDraft(EMPTY_CATEGORY_DRAFT);
    setEditingCategoryUuid('');
  }, []);

  const resetServiceDraft = useCallback((availableCategories = categories) => {
    setServiceDraft({
      ...EMPTY_SERVICE_DRAFT,
      categoryUuid: availableCategories[0]?.categoryUuid || ''
    });
    setEditingServiceUuid('');
  }, [categories]);

  const fetchCatalog = useCallback(async () => {
    if (!canManage) {
      setCategories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await request(PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICES_BY_CATEGORY, {
        method: 'GET',
        requestName: 'services-catalog-by-category'
      });

      const payload = response?.data;
      const nextCategories = Array.isArray(payload) ? payload : [];
      setCategories(nextCategories);
      setServiceDraft((previous) => (
        previous.categoryUuid || !nextCategories.length
          ? previous
          : { ...previous, categoryUuid: nextCategories[0].categoryUuid }
      ));
    } catch (error) {
      const message = resolveErrorMessage(error, 'Unable to load services catalog.');
      setErrorMessage(message);
      setCategories([]);
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [canManage, onToast, request]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const allServices = useMemo(
    () => categories.flatMap((category) => Array.isArray(category?.services) ? category.services : []),
    [categories]
  );

  const employeeNameById = useMemo(() => {
    const rows = Array.isArray(staffingEmployeesQuery.data) ? staffingEmployeesQuery.data : [];
    return rows.reduce((accumulator, employee) => {
      accumulator[employee.employeeUuid] = employee.name;
      return accumulator;
    }, {});
  }, [staffingEmployeesQuery.data]);

  const summary = useMemo(() => {
    const activeServices = allServices.filter(isActiveCatalogService).length;
    const durationValues = allServices
      .map((service) => Number(service?.appointmentDurationMins))
      .filter((value) => Number.isFinite(value) && value > 0);
    const priceValues = allServices
      .map((service) => Number(service?.basePrice))
      .filter((value) => Number.isFinite(value) && value >= 0);
    const averageDuration = durationValues.length
      ? Math.round(durationValues.reduce((total, value) => total + value, 0) / durationValues.length)
      : 0;
    const averagePrice = priceValues.length
      ? Math.round(priceValues.reduce((total, value) => total + value, 0) / priceValues.length)
      : 0;

    return {
      totalCategories: categories.length,
      totalServices: allServices.length,
      activeServices,
      averageDuration,
      averagePrice
    };
  }, [allServices, categories.length]);

  const filterCounts = useMemo(() => {
    const activeCount = allServices.filter(isActiveCatalogService).length;
    const pausedCount = allServices.filter((service) => String(service?.catalogStatus || '').toUpperCase() === 'INACTIVE').length;
    return {
      ALL: allServices.length,
      ACTIVE: activeCount,
      PAUSED: pausedCount
    };
  }, [allServices]);

  const filteredCategories = useMemo(() => {
    const normalizedQuery = String(searchQuery || '').trim().toLowerCase();
    const filterValue = String(statusFilter || 'ALL').toUpperCase();

    return categories.reduce((accumulator, category) => {
      const categoryStatus = String(category?.status || '').toUpperCase();
      const categoryMatchesQuery = !normalizedQuery || [category?.name, category?.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));

      const categoryMatchesStatus = filterValue === 'ALL'
        ? true
        : filterValue === 'ACTIVE'
          ? categoryStatus === 'ACTIVE'
          : categoryStatus === 'INACTIVE';

      const services = Array.isArray(category?.services) ? category.services : [];
      const nextServices = services.filter((service) => {
        const serviceMatchesStatus = matchesServiceStatusFilter(service?.catalogStatus, filterValue);
        const serviceMatchesQuery = !normalizedQuery || [
          service?.serviceName,
          service?.serviceDescription,
          formatBookingMode(service?.bookingMode),
          String(service?.priceDisplayType || '')
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));

        return serviceMatchesStatus && serviceMatchesQuery;
      });

      if (!nextServices.length && !(categoryMatchesQuery && categoryMatchesStatus)) {
        return accumulator;
      }

      accumulator.push({
        ...category,
        services: nextServices,
        serviceCount: nextServices.length
      });
      return accumulator;
    }, []);
  }, [categories, searchQuery, statusFilter]);

  const submitCategory = useCallback(async () => {
    const trimmedName = String(categoryDraft.name || '').trim();
    const trimmedDescription = String(categoryDraft.description || '').trim();

    if (!trimmedName) {
      onToast?.({ message: 'Category name is required.', variant: 'error' });
      return;
    }
    if (!canManage) {
      onToast?.({ message: 'Store session not found. Please login again.', variant: 'error' });
      return;
    }

    setIsSavingCategory(true);
    try {
      const endpoint = editingCategoryUuid
        ? PROFILE_STORES_APPOINTMENTS_CATEGORY(editingCategoryUuid)
        : PROFILE_STORES_APPOINTMENTS_CATEGORIES;
      const method = editingCategoryUuid ? 'PUT' : 'POST';
      await request(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        requestName: editingCategoryUuid ? 'services-category-update' : 'services-category-create',
        body: JSON.stringify({
          name: trimmedName,
          description: trimmedDescription || null
        })
      });
      onToast?.({
        message: editingCategoryUuid ? 'Category updated.' : 'Category created.',
        variant: 'success'
      });
      resetCategoryDraft();
      setIsCategoryModalOpen(false);
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to save category.'), variant: 'error' });
    } finally {
      setIsSavingCategory(false);
    }
  }, [canManage, categoryDraft.description, categoryDraft.name, editingCategoryUuid, fetchCatalog, onToast, request, resetCategoryDraft]);

  const performServiceSave = useCallback(async () => {
    const trimmedName = String(serviceDraft.serviceName || '').trim();
    const trimmedDescription = String(serviceDraft.serviceDescription || '').trim();
    const categoryUuid = String(serviceDraft.categoryUuid || '').trim();
    const normalizedPriceDisplayType = 'EXACT';
    const requiresConsultation = false;
    const isAddon = false;
    const assignableRoleIds = [];

    setIsSavingService(true);
    try {
      const endpoint = editingServiceUuid
        ? PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICE(editingServiceUuid)
        : PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICES;
      const method = editingServiceUuid ? 'PUT' : 'POST';
      await request(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        requestName: editingServiceUuid ? 'services-catalog-update' : 'services-catalog-create',
        body: JSON.stringify({
          categoryUuid,
          serviceName: trimmedName,
          serviceDescription: trimmedDescription || null,
          appointmentDurationMins: normalizeDuration(serviceDraft.appointmentDurationMins),
          bookingMode: 'APPOINTMENT',
          priceDisplayType: normalizedPriceDisplayType,
          basePrice: normalizeBasePrice(serviceDraft.basePrice, normalizedPriceDisplayType),
          maxBookingsPerSlot: normalizePositiveCount(serviceDraft.maxBookingsPerSlot, 1),
          selfBookable: true,
          requiresConsultation,
          isAddon,
          addonRequiresServiceUuid: null,
          assignableRoleIds
        })
      });
      onToast?.({
        message: editingServiceUuid ? 'Service updated.' : 'Service created.',
        variant: 'success'
      });
      resetServiceDraft();
      setIsServiceModalOpen(false);
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to save service.'), variant: 'error' });
    } finally {
      setIsSavingService(false);
    }
  }, [editingServiceUuid, fetchCatalog, onToast, request, resetServiceDraft, serviceDraft]);

  const submitService = useCallback(async () => {
    const trimmedName = String(serviceDraft.serviceName || '').trim();
    const categoryUuid = String(serviceDraft.categoryUuid || '').trim();

    if (!categoryUuid) {
      onToast?.({ message: 'Select a category before saving a service.', variant: 'error' });
      return;
    }
    if (!trimmedName) {
      onToast?.({ message: 'Service name is required.', variant: 'error' });
      return;
    }
    if (!canManage) {
      onToast?.({ message: 'Store session not found. Please login again.', variant: 'error' });
      return;
    }

    // Pricing safeguard: a ₹0 price almost always means the merchant hasn't set
    // one yet. Surface a popup and make them explicitly confirm before saving.
    const priceValue = Number(serviceDraft.basePrice);
    if (!Number.isFinite(priceValue) || priceValue <= 0) {
      // openConfirmDialog is declared later in the component; use the state
      // setters directly (stable, declared up top) to avoid a TDZ reference.
      setConfirmInput('');
      setConfirmDialog({
        title: 'Pricing isn’t set yet',
        description: 'This service has a price of ₹0, so it will be saved as free. Set a price first, or confirm to continue.',
        confirmLabel: 'Save as free',
        cancelLabel: 'Set a price',
        tone: 'primary',
        onConfirm: () => performServiceSave()
      });
      return;
    }

    await performServiceSave();
  }, [canManage, onToast, performServiceSave, serviceDraft]);

  const startEditCategory = useCallback((category) => {
    setEditingCategoryUuid(category?.categoryUuid || '');
    setCategoryDraft({
      name: category?.name || '',
      description: category?.description || '',
      isVisible: String(category?.status || '').toUpperCase() !== 'INACTIVE'
    });
    setIsCategoryModalOpen(true);
  }, []);

  const startEditService = useCallback((service) => {
    setEditingServiceUuid(service?.serviceUuid || '');
    setServiceDraft(buildServiceDraftFromService(service));
    setIsServiceModalOpen(true);
  }, []);

  const updateCategoryStatus = useCallback(async (category) => {
    const categoryUuid = category?.categoryUuid;
    if (!categoryUuid || !canManage) {
      return;
    }
    const nextStatus = String(category?.status || '').toUpperCase() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const actionKey = `status:${categoryUuid}`;
    setActiveActionKey(actionKey);
    try {
      await request(`${PROFILE_STORES_APPOINTMENTS_CATEGORY(categoryUuid)}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'services-category-status',
        body: JSON.stringify({ status: nextStatus })
      });
      onToast?.({
        message: nextStatus === 'ACTIVE' ? 'Category activated.' : 'Category deactivated.',
        variant: 'success'
      });
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to update category status.'), variant: 'error' });
    } finally {
      setActiveActionKey('');
    }
  }, [canManage, fetchCatalog, onToast, request]);

  const updateServiceStatus = useCallback(async (service, nextStatus) => {
    const serviceUuid = service?.serviceUuid;
    if (!serviceUuid || !canManage) {
      return;
    }
    const actionKey = `service-status:${serviceUuid}:${nextStatus}`;
    setActiveActionKey(actionKey);
    try {
      await request(PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICE_STATUS(serviceUuid), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'services-catalog-status',
        body: JSON.stringify({ status: nextStatus })
      });
      onToast?.({
        message: nextStatus === 'ACTIVE' ? 'Service activated.' : 'Service moved to inactive.',
        variant: 'success'
      });
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to update service status.'), variant: 'error' });
    } finally {
      setActiveActionKey('');
    }
  }, [canManage, fetchCatalog, onToast, request]);

  const archiveService = useCallback(async (service) => {
    const serviceUuid = service?.serviceUuid;
    if (!serviceUuid || !canManage) {
      return;
    }
    const actionKey = `service-archive:${serviceUuid}`;
    setActiveActionKey(actionKey);
    try {
      await request(PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICE(serviceUuid), {
        method: 'DELETE',
        requestName: 'services-catalog-archive'
      });
      onToast?.({ message: 'Service archived.', variant: 'success' });
      if (editingServiceUuid === serviceUuid) {
        resetServiceDraft();
      }
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to archive service.'), variant: 'error' });
    } finally {
      setActiveActionKey('');
    }
  }, [canManage, editingServiceUuid, fetchCatalog, onToast, request, resetServiceDraft]);

  const deleteCategory = useCallback(async (category) => {
    const categoryUuid = category?.categoryUuid;
    if (!categoryUuid || !canManage) {
      return;
    }

    const actionKey = `delete:${categoryUuid}`;
    setActiveActionKey(actionKey);
    try {
      await request(PROFILE_STORES_APPOINTMENTS_CATEGORY(categoryUuid), {
        method: 'DELETE',
        requestName: 'services-category-delete'
      });
      onToast?.({ message: 'Category deleted.', variant: 'success' });
      if (editingCategoryUuid === categoryUuid) {
        resetCategoryDraft();
      }
      if (serviceDraft.categoryUuid === categoryUuid) {
        resetServiceDraft(categories.filter((item) => item.categoryUuid !== categoryUuid));
      }
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to delete category.'), variant: 'error' });
    } finally {
      setActiveActionKey('');
    }
  }, [canManage, categories, editingCategoryUuid, fetchCatalog, onToast, request, resetCategoryDraft, resetServiceDraft, serviceDraft.categoryUuid]);

  const openCreateCategory = useCallback(() => {
    resetCategoryDraft();
    setIsCategoryModalOpen(true);
  }, [resetCategoryDraft]);

  const closeCategoryModal = useCallback(() => {
    setIsCategoryModalOpen(false);
    resetCategoryDraft();
  }, [resetCategoryDraft]);

  const openCreateService = useCallback(() => {
    if (!categories.length) {
      onToast?.({ message: 'Create a category first to add services.', variant: 'error' });
      resetCategoryDraft();
      setIsCategoryModalOpen(true);
      return;
    }
    resetServiceDraft(categories);
    setIsServiceModalOpen(true);
  }, [categories, onToast, resetCategoryDraft, resetServiceDraft]);

  const closeServiceModal = useCallback(() => {
    setIsServiceModalOpen(false);
    resetServiceDraft();
  }, [resetServiceDraft]);

  const openSequenceModal = useCallback(() => {
    setSequenceDraft(
      categories.map((category) => ({
        ...category,
        services: Array.isArray(category?.services) ? [...category.services] : []
      }))
    );
    setIsSequenceModalOpen(true);
  }, [categories]);

  const closeSequenceModal = useCallback(() => {
    if (isSavingSequence) {
      return;
    }
    setIsSequenceModalOpen(false);
    setSequenceDraft([]);
  }, [isSavingSequence]);

  const moveCategoryInDraft = useCallback((index, direction) => {
    setSequenceDraft((previous) => moveIndex(previous, index, direction === 'up' ? index - 1 : index + 1));
  }, []);

  const moveServiceInDraft = useCallback((categoryUuid, index, direction) => {
    setSequenceDraft((previous) => previous.map((category) => {
      if (category.categoryUuid !== categoryUuid) {
        return category;
      }
      return {
        ...category,
        services: moveIndex(category.services || [], index, direction === 'up' ? index - 1 : index + 1)
      };
    }));
  }, []);

  const submitSequenceOrder = useCallback(async () => {
    if (!canManage) {
      onToast?.({ message: 'Store session not found. Please login again.', variant: 'error' });
      return;
    }
    setIsSavingSequence(true);
    try {
      await request(PROFILE_STORES_APPOINTMENTS_CATEGORIES_SORT, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'services-category-sort',
        body: JSON.stringify({
          categories: sequenceDraft.map((category, index) => ({
            categoryUuid: category.categoryUuid,
            sortOrder: index
          }))
        })
      });

      for (const category of sequenceDraft) {
        const services = Array.isArray(category?.services) ? category.services : [];
        for (let index = 0; index < services.length; index += 1) {
          const service = services[index];
          await request(PROFILE_STORES_APPOINTMENTS_CATALOG_SERVICE(service.serviceUuid), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            requestName: 'services-catalog-sequence-update',
            body: JSON.stringify(buildServicePayloadFromRecord(service, category.categoryUuid, index))
          });
        }
      }

      onToast?.({ message: 'Sequence order updated.', variant: 'success' });
      setIsSequenceModalOpen(false);
      setSequenceDraft([]);
      await fetchCatalog();
    } catch (error) {
      onToast?.({ message: resolveErrorMessage(error, 'Unable to update sequence order.'), variant: 'error' });
    } finally {
      setIsSavingSequence(false);
    }
  }, [canManage, fetchCatalog, onToast, request, sequenceDraft]);

  const openConfirmDialog = useCallback((config) => {
    setConfirmInput('');
    setConfirmDialog(config);
  }, []);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(null);
    setConfirmInput('');
  }, []);

  const runConfirmDialog = useCallback(async () => {
    if (!confirmDialog?.onConfirm) {
      return;
    }
    if (confirmDialog.requireConfirmText && confirmInput.trim().toUpperCase() !== 'CONFIRM') {
      return;
    }
    await confirmDialog.onConfirm();
    setConfirmDialog(null);
    setConfirmInput('');
  }, [confirmDialog, confirmInput]);

  const hasCategories = categories.length > 0;

  if (isLoading) {
    return <CatalogLoadingSkeleton />;
  }

  if (errorMessage) {
    return (
      <section className="services-catalog" style={pageShellStyle}>
        <EmptyState
          icon={<AlertTriangle size={20} />}
          title="Couldn&apos;t load services catalog"
          description={errorMessage}
          action={{ label: 'Retry', onClick: fetchCatalog }}
        />
      </section>
    );
  }

  return (
    <section className="services-catalog services-catalog--redesign">
      <div className={`services-catalog__hero${hasCategories ? '' : ' services-catalog__hero--empty'}`}>
        <div className="services-catalog__hero-row">
          <div className="services-catalog__hero-copy">
            <h1 className="services-catalog__title">{title}</h1>
            <p className="services-catalog__subtitle">
              Organize everything you offer into categories, then add the services customers can book or queue for.
            </p>
          </div>
          <div className="services-catalog__hero-actions">
            <button type="button" onClick={openCreateCategory} className="catalog-button catalog-button--secondary">
              <Layers3 size={16} />
              Create category
            </button>
            <button type="button" onClick={openCreateService} className="catalog-button catalog-button--primary">
              <Plus size={16} />
              Create service
            </button>
          </div>
        </div>

        {!hasCategories ? (
          <div className="services-catalog__empty">
            <div className="services-catalog__empty-graphic" aria-hidden="true">
              <div className="services-catalog__empty-card" />
              <div className="services-catalog__empty-plus"><Plus size={24} /></div>
            </div>
            <div className="services-catalog__empty-copy">
              <div className="services-catalog__eyebrow">Get started</div>
              <h3>Build your service catalog</h3>
              <p>
                Start by creating a category, then add the services customers can book or queue for.
                Categories keep your menu organized and easy to browse.
              </p>
            </div>
            <button type="button" onClick={openCreateCategory} className="catalog-button catalog-button--primary catalog-button--large">
              <Layers3 size={18} />
              Create your first category
            </button>
            <div className="services-catalog__starter-row">
              <span>Popular starting points</span>
              {['Consultations', 'Core Services', 'Premium'].map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setCategoryDraft((previous) => ({ ...previous, name: label }));
                    setEditingCategoryUuid('');
                    setIsCategoryModalOpen(true);
                  }}
                  className="catalog-button catalog-button--chip"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {hasCategories ? (
        <>
          <div className="services-catalog__stats" aria-label="Services summary">
            {[
              {
                label: 'Categories',
                value: summary.totalCategories,
                icon: <Layers3 size={18} />,
                tone: 'green'
              },
              {
                label: 'Services',
                value: summary.totalServices,
                helper: `${summary.activeServices} active`,
                icon: <Package size={18} />,
                tone: 'cyan'
              },
              {
                label: 'Avg. duration',
                value: summary.averageDuration,
                helper: 'min',
                icon: <Clock3 size={18} />,
                tone: 'amber'
              },
              {
                label: 'Avg. price',
                value: summary.averagePrice,
                icon: <IndianRupee size={18} />,
                tone: 'green'
              }
            ].map((item) => (
              <article key={item.label} className="services-catalog__stat-card">
                <div className={`services-catalog__stat-icon services-catalog__stat-icon--${item.tone}`}>{item.icon}</div>
                <div>
                  <div className="services-catalog__stat-value">
                    {item.value}
                    {item.helper ? <span>{item.helper}</span> : null}
                  </div>
                  <div className="services-catalog__stat-label">
                    {item.label}
                    {item.label === 'Services' ? <strong> · {summary.activeServices} active</strong> : null}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="services-catalog__controls">
            <div className="services-catalog__segments" role="group" aria-label="Service status filter">
              {[
                { key: 'ALL', label: 'All' },
                { key: 'ACTIVE', label: 'Active' },
                { key: 'PAUSED', label: 'Paused' }
              ].map((item) => {
                const isActive = statusFilter === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setStatusFilter(item.key)}
                    className={`services-catalog__segment${isActive ? ' services-catalog__segment--active' : ''}`}
                  >
                    <span>{item.label}</span>
                    <strong>{filterCounts[item.key]}</strong>
                  </button>
                );
              })}
            </div>

            <div className="services-catalog__control-actions">
              <label className="services-catalog__search" aria-label="Filter services">
                <Search size={16} />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Filter services..."
                />
              </label>
              <button type="button" onClick={openSequenceModal} className="catalog-button catalog-button--secondary">
                <ArrowUpDown size={14} />
                Manage sequence
              </button>
              <button type="button" onClick={fetchCatalog} className="catalog-button catalog-button--secondary">
                <RefreshCcw size={14} />
                Refresh
              </button>
            </div>
          </div>

          <div className="services-catalog__list">
            {filteredCategories.length ? filteredCategories.map((category, index) => {
              const services = Array.isArray(category?.services) ? category.services : [];
              const isInactive = String(category?.status || '').toUpperCase() === 'INACTIVE';
              return (
                <section key={category.categoryUuid} className="catalog-category-card">
                  <div className="catalog-category-card__header">
                    <div className="catalog-category-card__main">
                      <div className="catalog-category-card__avatar" aria-hidden="true">
                        {String(category.name || 'C').charAt(0)}
                      </div>
                      <div className="catalog-category-card__copy">
                        <div className="catalog-category-card__title-row">
                          <span className="catalog-order">#{index + 1}</span>
                          <h4>{category.name}</h4>
                          <span className={`catalog-status-pill${isInactive ? ' catalog-status-pill--inactive' : ''}`}>
                            {category.status || 'ACTIVE'}
                          </span>
                        </div>
                        <p>{category.description || 'No category description yet.'}</p>
                      </div>
                    </div>
                    <div className="catalog-category-card__actions">
                      <span className="catalog-count-pill">{services.length} service{services.length === 1 ? '' : 's'}</span>
                      <button type="button" onClick={() => startEditCategory(category)} className="catalog-action catalog-action--icon" aria-label={`Edit ${category.name}`}>
                        <Edit3 size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openConfirmDialog({
                          title: isInactive ? 'Activate category?' : 'Deactivate category?',
                          description: isInactive
                            ? `This will activate "${category?.name || 'this category'}" and make its services available again. Type CONFIRM to continue.`
                            : `This will deactivate "${category?.name || 'this category'}" and hide its services from booking. Type CONFIRM to continue.`,
                          confirmLabel: isInactive ? 'Activate category' : 'Deactivate category',
                          tone: isInactive ? 'primary' : 'danger',
                          requireConfirmText: true,
                          onConfirm: () => updateCategoryStatus(category)
                        })}
                        disabled={activeActionKey === `status:${category.categoryUuid}`}
                        className={`catalog-action ${isInactive ? 'catalog-action--success' : 'catalog-action--danger'}`}
                      >
                        <Power size={14} />
                        {isInactive ? 'Activate' : 'Deactivate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openConfirmDialog({
                          title: 'Delete category?',
                          description: `This will delete "${category?.name || 'this category'}" and all services inside it. Please confirm before continuing.`,
                          confirmLabel: 'Delete category',
                          tone: 'danger',
                          onConfirm: () => deleteCategory(category)
                        })}
                        disabled={activeActionKey === `delete:${category.categoryUuid}`}
                        className="catalog-action catalog-action--danger catalog-action--icon"
                        aria-label={`Delete ${category.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="catalog-category-card__services">
                    {services.length ? services.map((service, serviceIndex) => {
                      const normalizedStatus = String(service.catalogStatus || 'ACTIVE').toUpperCase();
                      const serviceIsInactive = normalizedStatus === 'INACTIVE';
                      return (
                        <article key={service.serviceUuid} className="catalog-service-row">
                          <div className="catalog-service-row__order">
                            <span>#{serviceIndex + 1}</span>
                            <span className="catalog-service-row__drag" aria-hidden="true">::</span>
                          </div>
                          <div className="catalog-service-row__body">
                            <div className="catalog-service-row__content">
                              <div className="catalog-service-row__title-row">
                                <h5>{service.serviceName}</h5>
                                <span className={`catalog-status-pill catalog-status-pill--small${serviceIsInactive ? ' catalog-status-pill--inactive' : ''}`}>
                                  {normalizedStatus}
                                </span>
                              </div>
                              <p>{service.serviceDescription || 'Description will be added as the merchant fleshes out the service menu.'}</p>
                              <div className="catalog-service-row__meta">
                                <span><Clock3 size={14} />{service.appointmentDurationMins || 0} min</span>
                                <span><Tag size={14} />{formatBookingMode(service.bookingMode)}</span>
                                <span><IndianRupee size={14} />{formatServicePriceLabel(service)}</span>
                                <span><Package size={14} />{formatBookableByLabel(service)}</span>
                              </div>
                              <div className="catalog-service-row__chips">
                                <span className="catalog-chip catalog-chip--green">
                                  {isCustomerSelfBookableService(service) ? 'Self-booking' : 'Merchant-assisted'}
                                </span>
                                {service.requiresConsultation ? <span className="catalog-chip catalog-chip--amber">Required before booking</span> : null}
                                {service.isAddon ? <span className="catalog-chip">Add-on</span> : null}
                              </div>
                              {staffingEnabled ? (
                                <ServiceAssignmentChips
                                  serviceUuid={service.serviceUuid}
                                  employeeNameById={employeeNameById}
                                  onToast={onToast}
                                  onRequestRemove={openConfirmDialog}
                                />
                              ) : null}
                            </div>

                            <div className="catalog-service-row__actions">
                              <button type="button" onClick={() => startEditService(service)} className="catalog-action catalog-action--icon" aria-label={`Edit ${service.serviceName}`}>
                                <Edit3 size={14} />
                              </button>
                              {staffingEnabled && (
                                <button
                                  type="button"
                                  onClick={() => setAssignmentsForService(service)}
                                  className="catalog-action"
                                  title="Manage assigned staff"
                                >
                                  Staff
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openConfirmDialog({
                                  title: serviceIsInactive ? 'Activate service?' : 'Deactivate service?',
                                  description: serviceIsInactive
                                    ? `This will activate "${service?.serviceName || 'this service'}" and make it available for booking. Type CONFIRM to continue.`
                                    : `This will deactivate "${service?.serviceName || 'this service'}" and remove it from booking. Type CONFIRM to continue.`,
                                  confirmLabel: serviceIsInactive ? 'Activate service' : 'Deactivate service',
                                  tone: serviceIsInactive ? 'primary' : 'danger',
                                  requireConfirmText: true,
                                  onConfirm: () => updateServiceStatus(service, serviceIsInactive ? 'ACTIVE' : 'INACTIVE')
                                })}
                                disabled={
                                  activeActionKey === `service-status:${service.serviceUuid}:${serviceIsInactive ? 'ACTIVE' : 'INACTIVE'}`
                                  || (isInactive && serviceIsInactive)
                                }
                                className={`catalog-action ${serviceIsInactive ? 'catalog-action--success' : 'catalog-action--danger'}`}
                              >
                                <Power size={14} />
                                {serviceIsInactive ? 'Activate' : 'Deactivate'}
                              </button>
                              <button
                                type="button"
                                onClick={() => openConfirmDialog({
                                  title: 'Delete service?',
                                  description: `This will delete "${service?.serviceName || 'this service'}" from the active catalog view. Please confirm before continuing.`,
                                  confirmLabel: 'Delete service',
                                  tone: 'danger',
                                  onConfirm: () => archiveService(service)
                                })}
                                disabled={activeActionKey === `service-archive:${service.serviceUuid}`}
                                className="catalog-action catalog-action--icon"
                                aria-label={`Archive ${service.serviceName}`}
                              >
                                <Archive size={14} />
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    }) : (
                      <div className="services-catalog__notice">
                        No services in this category match the current filter.
                      </div>
                    )}
                  </div>
                </section>
              );
            }) : (
              <div className="services-catalog__notice">
                No categories or services match this view right now. Try changing the filter or search query.
              </div>
            )}
          </div>
        </>
      ) : null}

      {isSequenceModalOpen ? (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Manage sequence order"
              onClick={closeSequenceModal}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 39,
                background: 'rgba(2, 6, 23, 0.74)',
                backdropFilter: 'blur(10px)',
                display: 'grid',
                placeItems: 'center',
                padding: 24
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: '100%',
                  maxWidth: 760,
                  borderRadius: 22,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  background: 'rgba(7, 7, 7, 0.98)',
                  boxShadow: '0 26px 56px rgba(0, 0, 0, 0.4)',
                  overflow: 'hidden'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', padding: 20 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Manage sequence order</h3>
                    <p style={{ margin: '4px 0 0', color: 'var(--text-muted, #94a3b8)', lineHeight: 1.55, maxWidth: 460, fontSize: 14 }}>
                      Reorder categories and the services inside them. Save once you are happy with the final sequence.
                    </p>
                  </div>
                  <button type="button" onClick={closeSequenceModal} style={{ ...buttonStyle('default'), borderRadius: 14, padding: 10 }}>
                    <X size={16} />
                  </button>
                </div>
                <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.08)', padding: 20, display: 'grid', gap: 14, maxHeight: '68vh', overflowY: 'auto' }}>
                  {sequenceDraft.map((category, categoryIndex) => (
                    <div key={category.categoryUuid} style={{ borderRadius: 16, border: '1px solid rgba(148, 163, 184, 0.14)', background: 'rgba(10, 10, 10, 0.98)', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid rgba(148, 163, 184, 0.08)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: 12, fontWeight: 700 }}>#{categoryIndex + 1}</span>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>{category.name}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" onClick={() => moveCategoryInDraft(categoryIndex, 'up')} disabled={categoryIndex === 0} style={{ ...buttonStyle('default'), padding: 10 }}>
                            <ArrowUp size={14} />
                          </button>
                          <button type="button" onClick={() => moveCategoryInDraft(categoryIndex, 'down')} disabled={categoryIndex === sequenceDraft.length - 1} style={{ ...buttonStyle('default'), padding: 10 }}>
                            <ArrowDown size={14} />
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 0 }}>
                        {(category.services || []).map((service, serviceIndex) => (
                          <div key={service.serviceUuid} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '10px 14px', borderTop: serviceIndex === 0 ? 'none' : '1px solid rgba(148, 163, 184, 0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: 12, fontWeight: 700 }}>#{serviceIndex + 1}</span>
                              <div style={{ fontSize: 13, fontWeight: 700 }}>{service.serviceName}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button type="button" onClick={() => moveServiceInDraft(category.categoryUuid, serviceIndex, 'up')} disabled={serviceIndex === 0} style={{ ...buttonStyle('default'), padding: 10 }}>
                                <ArrowUp size={14} />
                              </button>
                              <button type="button" onClick={() => moveServiceInDraft(category.categoryUuid, serviceIndex, 'down')} disabled={serviceIndex === (category.services || []).length - 1} style={{ ...buttonStyle('default'), padding: 10 }}>
                                <ArrowDown size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.08)', padding: '16px 20px', display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                  <button type="button" onClick={closeSequenceModal} style={buttonStyle('default')}>Cancel</button>
                  <button
                    type="button"
                    onClick={submitSequenceOrder}
                    disabled={isSavingSequence}
                    style={{
                      ...buttonStyle('primary'),
                      borderRadius: 14,
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, rgba(255, 138, 94, 0.96), rgba(243, 119, 77, 0.88))',
                      color: '#02140a',
                      border: '1px solid rgba(110, 231, 183, 0.45)',
                      opacity: isSavingSequence ? 0.7 : 1
                    }}
                  >
                    <ArrowUpDown size={16} />
                    {isSavingSequence ? 'Saving…' : 'Save sequence'}
                  </button>
                </div>
              </div>
            </div>
      ) : null}

      {confirmDialog ? (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={confirmDialog.title}
              onClick={closeConfirmDialog}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 60,
                background: 'rgba(2, 6, 23, 0.76)',
                backdropFilter: 'blur(8px)',
                display: 'grid',
                placeItems: 'center',
                padding: 24
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: '100%',
                  maxWidth: 460,
                  borderRadius: 20,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  background: 'rgba(7, 7, 7, 0.98)',
                  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                  overflow: 'hidden'
                }}
              >
                <div style={{ padding: 20, display: 'grid', gap: 10 }}>
                  <div style={{ display: 'inline-flex', width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center', background: confirmDialog.tone === 'danger' ? 'rgba(127, 29, 29, 0.24)' : 'rgba(243, 119, 77, 0.14)', color: confirmDialog.tone === 'danger' ? '#fca5a5' : '#F3774D' }}>
                    {confirmDialog.requireConfirmText ? <Power size={18} /> : <AlertTriangle size={18} />}
                  </div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{confirmDialog.title}</h3>
                  <p style={{ margin: 0, color: 'var(--text-muted, #94a3b8)', lineHeight: 1.55, fontSize: 14 }}>{confirmDialog.description}</p>
                  {confirmDialog.requireConfirmText ? (
                    <input
                      type="text"
                      value={confirmInput}
                      onChange={(event) => setConfirmInput(event.target.value)}
                      placeholder="Type CONFIRM"
                      autoFocus
                      autoComplete="off"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && confirmInput.trim().toUpperCase() === 'CONFIRM') {
                          runConfirmDialog();
                        }
                      }}
                      style={{ ...inputStyle, marginTop: 4 }}
                    />
                  ) : null}
                </div>
                <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.08)', padding: '16px 20px', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button type="button" onClick={closeConfirmDialog} style={buttonStyle('default')}>{confirmDialog.cancelLabel || 'Cancel'}</button>
                  <button
                    type="button"
                    onClick={runConfirmDialog}
                    disabled={confirmDialog.requireConfirmText && confirmInput.trim().toUpperCase() !== 'CONFIRM'}
                    style={{
                      ...buttonStyle(confirmDialog.tone === 'danger' ? 'danger' : 'primary'),
                      borderRadius: 14,
                      padding: '12px 16px',
                      opacity: confirmDialog.requireConfirmText && confirmInput.trim().toUpperCase() !== 'CONFIRM' ? 0.5 : 1,
                      cursor: confirmDialog.requireConfirmText && confirmInput.trim().toUpperCase() !== 'CONFIRM' ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {confirmDialog.requireConfirmText
                      ? <Power size={16} />
                      : confirmDialog.tone === 'danger'
                        ? <Trash2 size={16} />
                        : <CheckCircle2 size={16} />}
                    {confirmDialog.confirmLabel}
                  </button>
                </div>
              </div>
            </div>
      ) : null}

      {isCategoryModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editingCategoryUuid ? 'Edit category' : 'Create category'}
          onClick={closeCategoryModal}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            background: 'rgba(2, 6, 23, 0.72)',
            backdropFilter: 'blur(10px)',
            display: 'grid',
            placeItems: 'center',
            padding: 24
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 720,
              borderRadius: 20,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background: 'rgba(7, 7, 7, 0.99)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.34)',
              overflow: 'hidden'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', padding: 20 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(243, 119, 77, 0.14)',
                    border: '1px solid rgba(243, 119, 77, 0.22)',
                    color: '#4ade80'
                  }}
                >
                  <Layers3 size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                    {editingCategoryUuid ? 'Edit category' : 'Create category'}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-muted, #94a3b8)', lineHeight: 1.55, maxWidth: 360, fontSize: 14 }}>
                    Group related services together so customers can browse them easily.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeCategoryModal}
                style={{
                  ...buttonStyle('default'),
                  borderRadius: 14,
                  padding: 10
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.08)', padding: 20, display: 'grid', gap: 16 }}>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                  Category name
                </span>
                <input
                  type="text"
                  value={categoryDraft.name}
                  onChange={(event) => setCategoryDraft((previous) => ({ ...previous, name: event.target.value }))}
                  placeholder="Consultations"
                  className="catalog-modal__field"
                  style={inputStyle}
                  maxLength={100}
                />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Description
                  </span>
                  <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: 13 }}>Optional</span>
                </div>
                <textarea
                  value={categoryDraft.description}
                  onChange={(event) => setCategoryDraft((previous) => ({ ...previous, description: event.target.value }))}
                  placeholder="Intake and advisory sessions before a booking is confirmed."
                  className="catalog-modal__field catalog-modal__field--textarea"
                  style={textAreaStyle}
                  maxLength={500}
                />
              </label>

              <div
                style={{
                  borderRadius: 16,
                  border: '1px solid rgba(148, 163, 184, 0.14)',
                  background: 'rgba(10, 10, 10, 0.98)',
                  padding: '16px 18px',
                  display: 'grid',
                  gap: 10
                }}
              >
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Visibility
                  </span>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>{categoryDraft.isVisible ? 'Active' : 'Inactive'}</div>
                      <div style={{ color: 'var(--text-muted, #94a3b8)', lineHeight: 1.5 }}>
                        Show this category to customers
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCategoryDraft((previous) => ({ ...previous, isVisible: !previous.isVisible }))}
                      style={{
                        width: 54,
                        height: 32,
                        borderRadius: 999,
                        border: '1px solid rgba(148, 163, 184, 0.18)',
                        background: categoryDraft.isVisible ? 'rgba(243, 119, 77, 0.28)' : 'rgba(39, 39, 42, 0.95)',
                        padding: 4,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: categoryDraft.isVisible ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: categoryDraft.isVisible ? '#4ade80' : '#cbd5e1',
                          display: 'block'
                        }}
                      />
                    </button>
                  </div>
              </div>
            </div>

            <div
              style={{
                borderTop: '1px solid rgba(148, 163, 184, 0.08)',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ color: 'var(--text-muted, #94a3b8)', fontSize: 13 }}>
                You can reorder categories anytime.
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button type="button" onClick={closeCategoryModal} style={buttonStyle('default')}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitCategory}
                  disabled={isSavingCategory}
                  style={{
                    ...buttonStyle('primary'),
                    borderRadius: 14,
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, rgba(255, 138, 94, 0.96), rgba(243, 119, 77, 0.88))',
                    color: '#02140a',
                    border: '1px solid rgba(110, 231, 183, 0.45)',
                    opacity: isSavingCategory ? 0.7 : 1,
                    cursor: isSavingCategory ? 'progress' : 'pointer',
                    fontWeight: 800
                  }}
                >
                  <Layers3 size={16} />
                  {isSavingCategory ? 'Saving…' : editingCategoryUuid ? 'Save category' : 'Create category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isServiceModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editingServiceUuid ? 'Edit service' : 'Create service'}
          onClick={closeServiceModal}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 45,
            background: 'rgba(2, 6, 23, 0.76)',
            backdropFilter: 'blur(10px)',
            display: 'grid',
            placeItems: 'center',
            padding: 24,
            overflowY: 'auto'
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 920,
              borderRadius: 20,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background: 'rgba(7, 7, 7, 0.99)',
              boxShadow: '0 22px 44px rgba(0, 0, 0, 0.36)',
              overflow: 'hidden'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', padding: 20 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(243, 119, 77, 0.14)',
                    border: '1px solid rgba(243, 119, 77, 0.22)',
                    color: '#4ade80'
                  }}
                >
                  <Package size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>
                    {editingServiceUuid ? 'Edit service' : 'Create service'}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-muted, #94a3b8)', lineHeight: 1.55, maxWidth: 420, fontSize: 14 }}>
                    Add a bookable service and keep the setup clean and simple.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeServiceModal}
                style={{
                  ...buttonStyle('default'),
                  borderRadius: 14,
                  padding: 10
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.08)', padding: 20, display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(240px, 1fr))', gap: 14 }}>
                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Category
                  </span>
                  <select
                    value={serviceDraft.categoryUuid}
                    onChange={(event) => setServiceDraft((previous) => ({ ...previous, categoryUuid: event.target.value }))}
                    className="catalog-modal__field catalog-modal__field--select"
                    style={inputStyle}
                  >
                    {categories.map((category) => (
                      <option key={category.categoryUuid} value={category.categoryUuid}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div />
              </div>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                  Service name
                </span>
                <input
                  type="text"
                  value={serviceDraft.serviceName}
                  onChange={(event) => setServiceDraft((previous) => ({ ...previous, serviceName: event.target.value }))}
                  placeholder="Standard Service"
                  className="catalog-modal__field"
                  style={inputStyle}
                  maxLength={100}
                />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Description
                  </span>
                  <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: 13 }}>Optional</span>
                </div>
                <textarea
                  value={serviceDraft.serviceDescription}
                  onChange={(event) => setServiceDraft((previous) => ({ ...previous, serviceDescription: event.target.value }))}
                  placeholder="A standard 45-minute session available to all customers."
                  className="catalog-modal__field catalog-modal__field--textarea"
                  style={textAreaStyle}
                  maxLength={500}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))', gap: 14 }}>
                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Duration
                  </span>
                  <input
                    type="number"
                    min={5}
                    max={480}
                    value={serviceDraft.appointmentDurationMins}
                    onChange={(event) => setServiceDraft((previous) => ({ ...previous, appointmentDurationMins: event.target.value }))}
                    className="catalog-modal__field"
                    style={inputStyle}
                  />
                </label>

                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Max per slot
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={serviceDraft.maxBookingsPerSlot}
                    onChange={(event) => setServiceDraft((previous) => ({ ...previous, maxBookingsPerSlot: event.target.value }))}
                    className="catalog-modal__field"
                    style={inputStyle}
                  />
                </label>

                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted, #94a3b8)' }}>
                    Price
                  </span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={serviceDraft.basePrice}
                    onChange={(event) => setServiceDraft((previous) => ({ ...previous, basePrice: event.target.value, priceDisplayType: 'EXACT' }))}
                    className="catalog-modal__field"
                    style={inputStyle}
                  />
                </label>
              </div>
            </div>

            <div
              style={{
                borderTop: '1px solid rgba(148, 163, 184, 0.08)',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <button type="button" onClick={closeServiceModal} style={buttonStyle('default')}>
                Cancel
              </button>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button type="button" onClick={closeServiceModal} style={buttonStyle('default')}>
                  Save as draft
                </button>
                <button
                  type="button"
                  onClick={submitService}
                  disabled={isSavingService}
                  style={{
                    ...buttonStyle('primary'),
                    borderRadius: 14,
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, rgba(255, 138, 94, 0.96), rgba(243, 119, 77, 0.88))',
                    color: '#02140a',
                    border: '1px solid rgba(110, 231, 183, 0.45)',
                    opacity: isSavingService ? 0.7 : 1,
                    cursor: isSavingService ? 'progress' : 'pointer',
                    fontWeight: 800
                  }}
                >
                  <CheckCircle2 size={16} />
                  {isSavingService ? 'Saving…' : editingServiceUuid ? 'Save service' : 'Create service'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {staffingEnabled && assignmentsForService ? (
        <AssignmentManagerModal
          open
          mode="by-service"
          serviceUuid={assignmentsForService.serviceUuid}
          serviceName={assignmentsForService.serviceName || 'service'}
          onClose={() => setAssignmentsForService(null)}
          onToast={(toast) => onToast?.({
            message: toast.message,
            variant: toast.type === 'success' ? 'success' : 'error',
          })}
        />
      ) : null}
    </section>
  );
};

export default StoreServicesCatalog;
